import * as React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { XMarkIcon, BellIcon } from '@heroicons/react/24/outline';
import type { ToastData } from '@/context/ToastContext';

interface ToastProps extends ToastData {
  onClose: (id: string) => void;
}

/** Single toast notification with auto-dismiss timer + optional action button. Rendered by `ToastContainer`. */
export const Toast: React.FC<ToastProps> = ({
  id,
  title,
  subtitle,
  icon,
  action,
  secondaryAction,
  onClose,
}) => {
  const hasActions = !!action || !!secondaryAction;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.95 }}
      className={clsx(
        'relative flex flex-col overflow-hidden',
        // Dimensions: Full width of parent container
        'w-full',
        // Height logic
        hasActions ? 'h-auto min-h-[110px] py-4' : 'h-toast justify-center',
        // Styling
        'bg-purple-430 shadow-2xl',
        // Rounded Corners: TL, BL, BR = 20px, TR = 5px
        'rounded-b-20 rounded-tl-20 rounded-tr-5',
        'border border-purple-300/20',
        'pl-4 pr-8'
      )}
    >
      {/* Main Content Row */}
      <div className='flex items-center gap-4'>
        {/* Icon Container */}
        <div className='flex size-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm'>
          {typeof icon === 'string' ? (
            <img src={icon} alt='' className='size-full rounded-full object-cover' />
          ) : (
            // Fallback Icon
            icon || <BellIcon className='size-6 text-purple-600' />
          )}
        </div>

        {/* Text Content */}
        <div className='flex flex-col justify-center'>
          <h4 className='font-sans text-toast-header font-medium leading-tight text-white'>
            {title}
          </h4>
          <p className='mt-0.5 font-sans text-toast-sub font-normal leading-snug text-white/90'>
            {subtitle}
          </p>
        </div>
      </div>

      {/* Actions Row (Rendered only if actions exist) */}
      {hasActions && (
        <div className='mt-3 flex items-center gap-6 pl-[64px]'>
          {action && (
            <button
              onClick={() => {
                action.onClick();
                onClose(id);
              }}
              className={clsx(
                'h-[32px] rounded-full px-6',
                'bg-purple-850 hover:bg-purple-900', // Dark purple button
                'font-sans text-13 font-bold uppercase tracking-wide text-white',
                'transition-transform active:scale-95'
              )}
            >
              {action.label}
            </button>
          )}

          {secondaryAction && (
            <button
              onClick={() => {
                secondaryAction.onClick();
                onClose(id);
              }}
              className='font-sans text-13 font-semibold text-white underline decoration-solid underline-offset-4 hover:opacity-80'
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}

      {/* Close Button */}
      <button
        onClick={() => onClose(id)}
        className='absolute right-3 top-3 p-1 text-white/50 transition-colors hover:text-white'
      >
        <XMarkIcon className='size-5' />
      </button>
    </motion.div>
  );
};
